<script setup lang="ts">
/**
 * Routing choices for a stream group: aggregator + % + state + parent (failover).
 * Master-detail opens domains / sender names / templates scoped to routing_choice_id.
 */
import { computed, onMounted, ref, watch } from 'vue';
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxLookup,
  DxMasterDetail,
  DxScrolling,
  DxSelection,
  DxSorting,
  type DxDataGridTypes,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import {
  bottoGridPaging,
  getAggregatorsLookup,
  showError,
} from '@botto/core';
import {
  createRoutingChoicesAdapter,
  createRoutingChoicesStore,
} from '../../api';
import type { RoutingChoice } from '../../api';
import RoutingChoiceMasterDetailPanel from './RoutingChoiceMasterDetailPanel.vue';

const { paging, pager } = bottoGridPaging({ pageSize: 20 });

const props = defineProps<{
  groupId: number;
}>();

const gridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);
const choicesStore = computed(() => createRoutingChoicesStore(props.groupId));
const parentOptions = ref<{ id: number; label: string }[]>([]);
const selectedRowKey = ref<number | null>(null);
const pendingParentId = ref<number | null>(null);

const aggregatorsLookup = getAggregatorsLookup();
const choicesAdapter = computed(() => createRoutingChoicesAdapter(props.groupId));
const stateItems = ['ACTIVE', 'INACTIVE'];

function aggregatorDisplay(item: { id: number }) {
  return aggregatorsLookup.labelOf(item.id);
}

function parentDisplay(item: { id: number; label: string }) {
  return item.label;
}

async function loadParentOptions() {
  if (!Number.isFinite(props.groupId)) return;
  try {
    const data = await choicesAdapter.value.list({
      offset: 0,
      limit: 1000,
      sortName: 'id',
      sortDirection: 'asc',
    });
    parentOptions.value = data.items
      .filter((g): g is typeof g & { id: number } => g.id != null)
      .map((g) => ({
        id: g.id,
        label: `${aggregatorsLookup.labelOf(g.aggregator_id) || 'агрегатор'} (#${g.id})`,
      }));
  } catch (e) {
    showError(e);
  }
}

async function refresh() {
  await loadParentOptions();
  await gridRef.value?.instance?.refresh();
}

function onSelectionChanged(e: DxDataGridTypes.SelectionChangedEvent) {
  const key = e.selectedRowKeys?.[0];
  selectedRowKey.value = key != null ? Number(key) : null;
}

function onInitNewRow(e: DxDataGridTypes.InitNewRowEvent) {
  e.data.group_id = props.groupId;
  e.data.state = 'ACTIVE';
  e.data.percentage = 100;
  e.data.parent_id = pendingParentId.value;
  pendingParentId.value = null;
}

function addRootRow() {
  pendingParentId.value = null;
  void gridRef.value?.instance?.addRow();
}

function addFailoverRow() {
  if (selectedRowKey.value == null) {
    showError(new Error('Выберите агрегатор, для которого добавить запасной (parent)'));
    return;
  }
  pendingParentId.value = selectedRowKey.value;
  void gridRef.value?.instance?.addRow();
}

watch(
  () => props.groupId,
  () => {
    selectedRowKey.value = null;
    void loadParentOptions();
    void refresh();
  },
  { immediate: true },
);

onMounted(() => {
  void aggregatorsLookup.ensureLoaded();
});

defineExpose({ refresh });
</script>

<template>
  <div class="routing-choices-master-detail">
    <div class="toolbar">
      <DxButton text="Добавить агрегатор" icon="add" type="default" @click="addRootRow" />
      <DxButton
        text="Добавить запасной"
        icon="add"
        styling-mode="outlined"
        :disabled="selectedRowKey == null"
        hint="Запасной агрегатор (parent_id) на случай отключения текущего"
        @click="addFailoverRow"
      />
      <DxButton text="Обновить" icon="refresh" styling-mode="outlined" @click="refresh" />
    </div>

    <DxDataGrid
      id="routing-choices-grid"
      ref="gridRef"
      :data-source="choicesStore"
      key-expr="id"
      :show-borders="true"
      :show-row-lines="true"
      :column-auto-width="true"
      :hover-state-enabled="true"
      :repaint-changes-only="true"
      :selected-row-keys="selectedRowKey != null ? [selectedRowKey] : []"
      :paging="paging"
      :pager="pager"
      @selection-changed="onSelectionChanged"
      @init-new-row="onInitNewRow"
      @row-inserted="loadParentOptions"
      @row-updated="loadParentOptions"
      @row-removed="loadParentOptions"
    >
      <DxSelection mode="single" />
      <DxSorting mode="single" />
      <DxScrolling mode="standard" />

      <DxEditing
        :allow-adding="true"
        :allow-updating="true"
        :allow-deleting="true"
        mode="form"
      />

      <DxColumn data-field="id" caption="ID" :allow-editing="false" width="70" />
      <DxColumn
        data-field="aggregator_id"
        caption="Агрегатор"
        :calculate-display-value="(row: RoutingChoice) => aggregatorsLookup.labelOf(row.aggregator_id)"
      >
        <DxLookup
          :data-source="aggregatorsLookup.items.value"
          value-expr="id"
          :display-expr="aggregatorDisplay"
        />
      </DxColumn>
      <DxColumn data-field="state" caption="Статус">
        <DxLookup :data-source="stateItems" />
      </DxColumn>
      <DxColumn
        data-field="percentage"
        caption="%"
        data-type="number"
        :editor-options="{ min: 0, max: 100 }"
        width="90"
      />
      <DxColumn
        data-field="parent_id"
        caption="Родитель (failover)"
        :calculate-display-value="(row: RoutingChoice) => {
          if (row.parent_id == null) return '';
          const opt = parentOptions.find((p) => p.id === row.parent_id);
          return opt?.label ?? `#${row.parent_id}`;
        }"
      >
        <DxLookup
          :data-source="parentOptions"
          value-expr="id"
          :display-expr="parentDisplay"
          :allow-clearing="true"
        />
      </DxColumn>

      <DxMasterDetail :enabled="true" template="detail" />
      <template #detail="{ data: row }">
        <RoutingChoiceMasterDetailPanel
          :group-id="groupId"
          :routing-choice-id="row.data.id"
          :aggregator-id="row.data.aggregator_id"
        />
      </template>
    </DxDataGrid>
  </div>
</template>

<style scoped>
.routing-choices-master-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
}

.toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

#routing-choices-grid {
  height: 620px;
}

:deep(.dx-datagrid-rowsview .dx-master-detail-row:not(.dx-datagrid-edit-form) > .dx-datagrid-group-space),
:deep(.dx-datagrid-rowsview .dx-master-detail-row:not(.dx-datagrid-edit-form) .dx-master-detail-cell) {
  background-color: transparent;
}
</style>
